import { MarkerLabelFieldTypeEnum } from '@/__generated__/graphql';
import {
  AllPointOfInterestTemplateDocument,
  useUpdatePoiReviewPanelConfigMutation,
} from '@/__generated__/graphql';
import { REVIEW_PANEL_BLISTER_FIELDS, REVIEW_PANEL_POI_FIELDS } from './poi-field-definitions';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { abyssColors } from '@/theme/colors';
import { formatEnumForDisplay } from '@/utils';
import { Add, Edit } from '@mui/icons-material';
import {
  Autocomplete,
  Button,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useAtom } from 'jotai';
import { useMemo, useState } from 'react';
import { selectedPOIType } from '../../state';
import { dropDownStyles } from '../styles';
import { FullWidthPanel } from './styles';


const MAX_FIELDS = 8;

const BLISTER_PIT_DEFAULT_FIELDS: { key: string; type: MarkerLabelFieldTypeEnum }[] = [
  { key: 'maxHeight', type: MarkerLabelFieldTypeEnum.Blister },
  { key: 'residualCorrosionAllowance', type: MarkerLabelFieldTypeEnum.Blister },
  { key: 'wallLoss', type: MarkerLabelFieldTypeEnum.Blister },
  { key: 'measurementConfidence', type: MarkerLabelFieldTypeEnum.Blister },
  { key: 'distanceConfidence', type: MarkerLabelFieldTypeEnum.Blister },
  { key: 'deckRelativeHeight', type: MarkerLabelFieldTypeEnum.PointOfInterest },
  { key: 'paintRegion', type: MarkerLabelFieldTypeEnum.PointOfInterest },
  { key: 'deckName', type: MarkerLabelFieldTypeEnum.PointOfInterest },
];

const POI_DEFAULT_FIELDS: { key: string; type: MarkerLabelFieldTypeEnum }[] = [
  { key: 'deckRelativeHeight', type: MarkerLabelFieldTypeEnum.PointOfInterest },
  { key: 'paintRegion', type: MarkerLabelFieldTypeEnum.PointOfInterest },
  { key: 'deckName', type: MarkerLabelFieldTypeEnum.PointOfInterest },
];

export const PoiReviewPanelPanel = () => {
  const [selectedType, setSelectedType] = useAtom(selectedPOIType);
  const [mode, setMode] = useState<'ReadOnly' | 'Edit'>('ReadOnly');
  const [updateConfig, { loading }] = useUpdatePoiReviewPanelConfigMutation({
    refetchQueries: [AllPointOfInterestTemplateDocument],
    awaitRefetchQueries: true,
  });

  const allTemplateFields = useMemo(() => {
    return selectedType?.fields ?? [];
  }, [selectedType]);

  const isBlisterOrPit = useMemo(
    () => selectedType?.key === 'blister' || selectedType?.key === 'pit',
    [selectedType?.key]
  );

  const fieldTypes = useMemo(() => {
    return Object.values(MarkerLabelFieldTypeEnum).map((type) => formatEnumForDisplay(type));
  }, []);

  const savedFields = useMemo((): { key: string; type: MarkerLabelFieldTypeEnum }[] => {
    const config = (selectedType)?.poiReviewPanelConfig?.fields as
      | { key: string; type: MarkerLabelFieldTypeEnum }[]
      | undefined;
    if (config && config.length > 0) return config;
    return isBlisterOrPit ? BLISTER_PIT_DEFAULT_FIELDS : POI_DEFAULT_FIELDS;
  }, [selectedType, isBlisterOrPit]);

  const [draftFields, setDraftFields] = useState<{ key: string; type: MarkerLabelFieldTypeEnum }[]>(
    savedFields
  );

  const reviewFields = mode === 'Edit' ? draftFields : savedFields;
  const atLimit = reviewFields.length >= MAX_FIELDS;

  const getFieldOptions = (type: MarkerLabelFieldTypeEnum | undefined | null): { key: string; name: string }[] => {
    if (type === MarkerLabelFieldTypeEnum.Blister) return REVIEW_PANEL_BLISTER_FIELDS;
    if (type === MarkerLabelFieldTypeEnum.PointOfInterest) return REVIEW_PANEL_POI_FIELDS;
    return allTemplateFields
      .filter((f): f is typeof f & { key: string } => f.key != null)
      .map((f) => ({ key: f.key, name: f.name }));
  };

  const getFieldValue = (
    key: string | undefined | null,
    type: MarkerLabelFieldTypeEnum | undefined | null
  ) => {
    if (!key) return undefined;
    const options = getFieldOptions(type);
    return options.find((f) => f.key === key);
  };

  const handleEnterEdit = () => {
    setDraftFields(savedFields);
    setMode('Edit');
  };

  const handleCancel = () => {
    setDraftFields(savedFields);
    setMode('ReadOnly');
  };

  const handleSave = async () => {
    if (!selectedType?.id) return;
    const result = await updateConfig({
      variables: {
        input: {
          pointOfInterestTemplateId: selectedType.id,
          poiReviewPanelConfig: {
            fields: draftFields.map(({ key, type }) => ({ key, type })),
          },
        },
      },
    });
    const saved = result.data?.updatePoiReviewPanelConfig?.poiReviewPanelConfig?.fields;
    if (saved) {
      setSelectedType({
        ...selectedType,
        poiReviewPanelConfig: { fields: saved } as any,
      } as any);
    }
    setMode('ReadOnly');
  };

  const handleAddField = () => {
    if (atLimit) return;
    setDraftFields([...draftFields, { type: MarkerLabelFieldTypeEnum.TemplateField, key: '' }]);
  };

  const handleDeleteField = (index: number) => {
    setDraftFields(draftFields.filter((_, i) => i !== index));
  };

  const handleTypeChange = (index: number, value: string | null) => {
    if (!value) return;
    const enumValue =
      Object.values(MarkerLabelFieldTypeEnum).find(
        (v) => v === value.toUpperCase().replaceAll(' ', '_')
      ) ?? MarkerLabelFieldTypeEnum.TemplateField;
    setDraftFields(
      draftFields.map((field, i) => (i === index ? { ...field, type: enumValue, key: '' } : field))
    );
  };

  const handleKeyChange = (index: number, key: string | null) => {
    if (key === null) return;
    setDraftFields(draftFields.map((field, i) => (i === index ? { ...field, key } : field)));
  };

  return (
    <>
      <Stack
        width="100%"
        marginTop="1.2rem"
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        height="4.8rem"
      >
        <Typography fontSize="2.2rem" fontWeight={500}>
          {`Review Panel Configuration for ${selectedType?.name}`}
        </Typography>
        {mode === 'Edit' ? (
          <Stack direction="row" spacing={1} sx={{ pb: 0.5 }}>
            <Button variant="contained" disabled={loading} onClick={handleSave}>
              {loading ? 'Saving...' : 'Save'}
            </Button>
            <Button variant="contained" disabled={loading} onClick={handleCancel}>
              Cancel
            </Button>
          </Stack>
        ) : (
          <Stack direction="row" spacing={0} sx={{ pb: 0.5 }}>
            <Tooltip title="Edit Review Panel Configuration" placement="top" arrow>
              <IconButton size="medium" onClick={handleEnterEdit}>
                <Edit
                  fontSize="inherit"
                  sx={{ cursor: 'pointer', color: abyssColors.primary[500] }}
                />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      </Stack>
      <FullWidthPanel>
        <Stack sx={{ flex: 1, gap: '3.2rem' }}>
          <Stack gap={1}>
            <Stack direction="row" alignItems="center" gap={2}>
              <Typography variant="h4">Fields</Typography>
              <Typography variant="caption" color="text.secondary">
                ({reviewFields.length}/{MAX_FIELDS})
              </Typography>
            </Stack>
            <Stack gap={4} width="100%" marginTop={1}>
              {reviewFields.map((field, index) => (
                <Stack key={`review-field-${index}-${field.key}-${field.type}`} direction="row">
                  <Stack direction="row" alignItems="center" gap={2} width="30rem">
                    <Typography variant="h5">Type</Typography>
                    <Autocomplete
                      id={`review-field-type-${index}`}
                      disableClearable
                      disabled={mode === 'ReadOnly'}
                      options={fieldTypes}
                      value={field.type ? formatEnumForDisplay(field.type) : undefined}
                      onChange={(_, value) => handleTypeChange(index, value)}
                      getOptionLabel={(option) => option}
                      renderInput={(params) => (
                        <TextField {...params} fullWidth variant="standard" placeholder="" />
                      )}
                      sx={{ ...dropDownStyles, width: '100%' }}
                    />
                  </Stack>
                  <Stack direction="row" alignItems="center" gap={2} width="40rem">
                    <Typography variant="h5">Field</Typography>
                    <Autocomplete
                      id={`review-field-key-${index}-${field.key}`}
                      disableClearable
                      key={`${field.type}-${index}`}
                      disabled={mode === 'ReadOnly'}
                      options={getFieldOptions(field.type)}
                      value={getFieldValue(field.key, field.type)}
                      getOptionLabel={(option) => String(option.name)}
                      onChange={(_, value) => handleKeyChange(index, value?.key ?? null)}
                      renderInput={(params) => (
                        <TextField {...params} fullWidth variant="standard" placeholder="" />
                      )}
                      sx={{ ...dropDownStyles, width: '100%' }}
                    />
                  </Stack>
                  <Button
                    variant="contained"
                    startIcon={<AbyssIcon iconName="delete" />}
                    disabled={mode === 'ReadOnly'}
                    onClick={() => handleDeleteField(index)}
                  >
                    Delete
                  </Button>
                </Stack>
              ))}
              <Tooltip
                title={atLimit ? `Maximum of ${MAX_FIELDS} fields allowed` : ''}
                placement="right"
              >
                <span style={{ width: 'fit-content' }}>
                  <Button
                    sx={{ width: 'fit-content' }}
                    variant="contained"
                    disabled={mode === 'ReadOnly' || atLimit}
                    startIcon={<Add />}
                    onClick={handleAddField}
                  >
                    Add Field
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          </Stack>
        </Stack>
      </FullWidthPanel>
    </>
  );
};
